import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { TextareaComponent } from '../core/controls/textarea.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { DialogComponent } from '../core/panels/dialog.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';
import { ValidatorsEx } from '../../validators/validators-ex';

export interface CopyQueryResult {
  name: string;
  description: string;
}

@Component({
  selector: 'app-copy-query-dialog',
  standalone: true,
  imports: [
    ActionRowComponent,
    CommonModule,
    DialogComponent,
    ButtonComponent,
    InputComponent,
    TextareaComponent,
    ReactiveFormsModule,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <app-dialog #dialog [label]="label" [width]="width">
      <ng-template #content>
        <form (ngSubmit)="copyClick()" [formGroup]="fg">
          <app-required-fields-note />
          <div class="tw-py-4 tw-space-y-4">
            <app-input [control]="fg.controls.name" label="Name" />
            <app-textarea
              [control]="fg.controls.description"
              label="Description"
              rows="3"
            />
          </div>
          <app-action-row>
            <app-button
              icon="fa-light fa-check"
              right1
              [label]="buttonLabel"
              type="submit"
              [loading]="loading"
              [disabled]="!fg.valid"
            />
            <app-button (click)="close()" emphasis="Low" right2 label="Cancel" />
          </app-action-row>
        </form>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class CopyQueryDialogComponent {
  @ViewChild('dialog')
  dialog: DialogComponent;

  @Input()
  width = '35rem';

  @Input()
  label = 'Copy Query';

  @Input()
  buttonLabel = 'Copy';

  @Output()
  copy = new EventEmitter<CopyQueryResult>();

  fg = new FormGroup({
    name: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(8),
      ValidatorsEx.alphaNumericWith(
        '_-',
        'Query name can only contain letters, numbers, dashes, and underscores.',
      ),
    ]),
    description: new FormControl<string>(''),
  });

  loading = false;

  /** Prefills with the source query name/description (legacy ViaOne copy wizard behaviour). */
  show(originalName: string, originalDescription: string): void {
    this.dialog.visible = true;
    this.fg.controls.name.setValue(originalName ?? '');
    this.fg.controls.description.setValue(originalDescription ?? '');
    this.fg.controls.name.updateValueAndValidity();
    this.fg.controls.description.updateValueAndValidity();
  }

  close(): void {
    this.dialog.visible = false;
    this.loading = false;
  }

  copyClick(): void {
    if (this.fg.valid) {
      this.copy.emit({
        name: this.fg.controls.name.value ?? '',
        description: this.fg.controls.description.value ?? '',
      });
    }
  }

  setLoading(isLoading: boolean): void {
    this.loading = isLoading;
  }
}
